import type { paths } from "@src/packages/openapi/schema";
import type {
  CreateTestResultPayload,
  UpdateTestResultPayload,
} from "@src/packages/test-results/hooks/useTestResults";

type CycleOutcomeCreateBody = NonNullable<
  paths["/{tenantId}/cycles/{cycleId}/outcomes"]["post"]["requestBody"]
>["content"]["application/json"];

/**
 * The provenance a patient may claim for the outcome their beta result records.
 *
 * The API refuses any other value from a patient session — a patient cannot
 * author a clinic-sourced record — so sending `test_result` made every beta
 * recording fail with a generic error. It is also the truthful value: the
 * patient typed this number. The typed test-result row stays linked through
 * `sourceRecordId`, so the numeric source is not lost by telling the truth
 * about who entered it.
 */
export const PATIENT_BETA_OUTCOME_SOURCE_KIND: NonNullable<
  CycleOutcomeCreateBody["sourceKind"]
> = "patient_entered";

export type BetaTestResultInput = {
  cycleId: string;
  phaseId?: string | null;
  value: number;
  date: string;
  notes?: string | null;
};

/**
 * Persist a patient-entered beta as the existing typed test-result contract.
 * The result-series/order relationship still belongs to the backend outcome
 * work, but this keeps the numeric value out of the outcome note ledger.
 */
export function buildBetaTestResultPayload({
  cycleId,
  phaseId,
  value,
  date,
  notes,
}: BetaTestResultInput): CreateTestResultPayload {
  return {
    phaseId: phaseId ?? null,
    cycleId,
    name: "Beta hCG",
    type: "blood",
    bloodTestCategory: "hormonal",
    patientValue: String(value),
    valueType: "numeric",
    unit: "mIU/mL",
    date,
    notes: notes ?? null,
  } satisfies CreateTestResultPayload;
}

/**
 * Re-stamp the typed test result this recording already created.
 *
 * A failed outcome write leaves the modal open and editable, so the number the
 * patient finally saves can differ from the one the test-result row was created
 * with. The outcome links that row as its numeric source, so leaving it behind
 * would make the record say two different numbers — the notes reading 120 while
 * the linked result still says 12.
 */
export function buildBetaTestResultUpdate({
  value,
  notes,
}: Pick<BetaTestResultInput, "value" | "notes">): UpdateTestResultPayload {
  return {
    patientValue: String(value),
    valueType: "numeric",
    unit: "mIU/mL",
    notes: notes ?? null,
  } satisfies UpdateTestResultPayload;
}
